import React, {
  useEffect,
  useRef,
  useState,
  ComponentPropsWithoutRef,
  FC,
} from "react";
import { useMediaQuery } from "react-responsive";
import cn from "classnames";
import { SCREEN_BREAKPOINTS } from "src/constants/screenBreakpoints";

const TAB_HEIGHT = 60; // px

interface Props extends ComponentPropsWithoutRef<"section"> {
  title: string;
  titleClassName?: string;
}
export const FolderWrapper: FC<Props> = ({
  title,
  titleClassName,
  children,
  className,
  ...props
}) => {
  const [isShownTitle, setIsShownTitle] = useState(true);
  const ref = useRef<HTMLElement | null>(null);

  const isXLScreenBreakpoint = useMediaQuery({
    maxWidth: SCREEN_BREAKPOINTS.XL,
  });

  useEffect(() => {
    if (isXLScreenBreakpoint) return;

    const appEl = document.querySelector(".App");
    if (!appEl || !ref.current) return;

    const handleScroll = () => {
      const rect = ref.current.getBoundingClientRect();

      // upper boundary of the visible area, considering tab height
      const topVisibleBoundary = TAB_HEIGHT;
      // lower boundary of the visible area, considering tab height
      const bottomVisibleBoundary = appEl.clientHeight - TAB_HEIGHT;

      // check if the top of the element is within the visible area
      const isTopVisible = rect.top > topVisibleBoundary;
      // check if the top of the element is above the bottom boundary of the visible area
      const isBelowBottom = rect.top < bottomVisibleBoundary;

      setIsShownTitle(isTopVisible && isBelowBottom);
    };

    appEl.addEventListener("scroll", handleScroll);

    return () => {
      appEl.removeEventListener("scroll", handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section
      ref={ref}
      className={cn(
        "relative rounded-2xl xl:h-[calc(100vh-1.5rem)]", // 1.5 rem - padding in Stackable
        { "rounded-tl-none": isShownTitle },
        className,
      )}
      {...props}
    >
      <h4
        className={cn(
          "clip-path-custom absolute top-px left-0 flex items-end justify-start max-w-47.5 max-h-10 w-full h-full pl-4 pb-1 bg-inherit text-lg rounded-t-2xl transition-all duration-300 xl:max-w-80 xl:max-h-15 xl:clip-path-custom-xl xl:pl-6 xl:text-32",
          {
            "opacity-0 invisible": !isShownTitle,
            "opacity-100 -translate-y-full visible": isShownTitle,
          },
          titleClassName,
        )}
      >
        {title}
      </h4>

      {children}
    </section>
  );
};
